import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { PrintJobCreateResponse, GiantRelayPrintResponse } from '@shared/models/receipt-data';
import { LOCAL_PRINT_RELAY_URL } from '@bcmServices/local-print-relay-watchdog.service';
import print from 'print-js';

import { BcmFinancialRecord } from '@shared/models/bcm-financial-record';
import { InvoicePosition } from '@shared/models/invoice-position';
import { BcmPaymentType } from '@shared/models/person';
import { BcmNetworkPrinter } from '@shared/models/network-printer';
import {
    PrintJobRequest,
    ReceiptData,
    ReceiptPaymentType,
    ReceiptPosition,
    ReceiptServiceRecipient,
    ReceiptTax,
} from '@shared/models/receipt-data';

import { BcmService } from '@modules/bcm/bcm.service';
import { InvoicesService } from '@modules/bcm/accounting/invoices/invoices.service';
import { NetworkPrintersService } from '@modules/bcm/settings/network-printers/network-printers.service';
import { PrintJobsApiService } from '@bcmServices/api-services/print-jobs-api.service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { UserService } from '@core/services/user.service';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import {
    U2bInformationDialogComponent,
} from '@sharedComponents/dialogs/information-dialog/information-dialog.component';
import { dataURItoBlob } from '@shared/functions/datauri-to-blob';
import { TranslationService } from '@core/translation/translation.service';
import {
    resolveOriginalReceiptNumberForPrint,
    resolveReceiptPrintDate,
    resolveReceiptPrintPositions,
} from './financial-record-receipt-print.util';

const RECEIPT_PAYMENT_TYPE_MAP: Partial<Record<BcmPaymentType, ReceiptPaymentType>> = {
    [BcmPaymentType.Cash]: 'cash',
    [BcmPaymentType.CreditCard]: 'creditcard',
    [BcmPaymentType.EcCard]: 'eccard',
};

// Obergrenze für den print-jobs-Request. Verhindert, dass der Checkout-Dialog
// dauerhaft blockiert, wenn das Backend nicht antwortet.
const PRINT_JOB_REQUEST_TIMEOUT_MS = 20000;

// Obergrenze für die Relay-Antwort. Der Drucker-Timeout im Relay liegt bei 7s;
// 20s lassen dem Relay genug Zeit, einen eigenen Fehler zu melden, bevor wir
// abbrechen, damit der Dialog niemals hängen bleibt.
const LOCAL_PRINT_RELAY_TIMEOUT_MS = 20000;

@Injectable({providedIn: 'root'})
export class FinancialRecordPrintService {

    constructor(
        private _bcmService: BcmService,
        private _userService: UserService,
        private _bcmTenantService: BcmTenantService,
        private _invoicesService: InvoicesService,
        private _networkPrintersService: NetworkPrintersService,
        private _printJobsApiService: PrintJobsApiService,
        private _appNotificationService: AppNotificationService,
        private _matDialog: MatDialog,
    ) {}

    async printRecord(event: Event, record: BcmFinancialRecord): Promise<void> {
        event.stopPropagation();
        event.preventDefault();

        if (record.receipt) {
            await this.printReceiptForRecord(record);
            return;
        }

        await this._printInvoiceA4(record);
    }

    // ─── Receipt routing ────────────────────────────────────────────────────────

    async printReceiptForRecord(record: BcmFinancialRecord, printer?: BcmNetworkPrinter | null): Promise<boolean> {
        if (printer === null) {
            return this._printReceiptA4(record);
        }

        let preferredPrinter: BcmNetworkPrinter | null = printer ?? null;

        if (!preferredPrinter) {
            const printers = await this._networkPrintersService.loadAll().toPromise();
            const currentUserId = this._userService.userId;
            preferredPrinter = this._bcmTenantService.filterUsableReceiptPrinters(printers ?? [], currentUserId)[0] ?? null;
        }

        if (!preferredPrinter) {
            return this._printReceiptA4(record);
        }

        const receiptData = this._mapToReceiptData(record);
        const payload: PrintJobRequest = {
            printerId: preferredPrinter.id,
            receiptData,
        };

        const job = await firstValueFrom(
            this._printJobsApiService.create(payload).pipe(
                timeout({ each: PRINT_JOB_REQUEST_TIMEOUT_MS }),
                catchError(err => {
                    console.error('Druckauftrag fehlgeschlagen:', err);
                    this._appNotificationService.showError(
                        TranslationService.translate('bcm.financialRecords.notify.printJobFailed')
                    );
                    return of(null);
                }),
            ),
        );
        if (!job) {
            return false;
        }

        if (this._isGiantRelayPrintResponse(job)) {
            const sent = await this._sendToLocalPrintRelay(job);
            if (sent) {
                this._appNotificationService.showSuccess(TranslationService.translate('bcm.financialRecords.notify.printJobLocalRelaySent'));
            }
            return sent;
        }

        this._appNotificationService.showSuccess(TranslationService.translate('bcm.financialRecords.notify.printJobCreated'));
        return true;
    }

    // ─── A4 fallbacks ───────────────────────────────────────────────────────────

    private async _printInvoiceA4(record: BcmFinancialRecord): Promise<boolean> {
        let pdfBlob: Blob;
        try {
            pdfBlob = await this._invoicesService.getPdfFile(record.invoice).toPromise();
        } catch (_err) {
            this._showNoPdfDialog(record);
            return false;
        }

        if (!pdfBlob?.size) {
            this._showNoPdfDialog(record);
            return false;
        }

        print({printable: URL.createObjectURL(pdfBlob), documentTitle: this._buildDocumentTitle(record)});
        return true;
    }

    private _printReceiptA4(record: BcmFinancialRecord): boolean {
        const pdfBlob = dataURItoBlob(record.receipt.pdfDataUrl);

        if (!pdfBlob?.size) {
            this._showNoPdfDialog(record);
            return false;
        }

        print({printable: URL.createObjectURL(pdfBlob), documentTitle: this._buildDocumentTitle(record)});
        return true;
    }

    // ─── Mapping ────────────────────────────────────────────────────────────────

    private _mapToReceiptData(record: BcmFinancialRecord): ReceiptData {
        const receipt = record.receipt;
        const tenant = this._bcmService.tenant;
        const tseDoc = receipt.tseDocument;
        const tseTaxPositions = tseDoc?.taxPositions ?? tseDoc?.fiscalResponse?.taxPositions ?? [];
        const printPositions = resolveReceiptPrintPositions(receipt);

        // Prefer TSE-provided tax breakdown; fall back to calculating from positions
        // when TSE data is not yet available (e.g. newly created receipt without fiscal response).
        const taxes = tseTaxPositions.length > 0
            ? this._mapTaxes(tseTaxPositions)
            : this._mapTaxesFromPositions(printPositions);

        const totalTax = taxes.reduce((sum, t) => sum + t.taxValue, 0);

        return {
            tenant: {
                name: tenant?.name,
                street: tenant?.street,
                postCode: tenant?.postCode,
                city: tenant?.city,
                phone: tenant?.phone,
                fax: tenant?.fax,
                agbUrl: tenant?.agbUrl,
            },

            receiptNumber: receipt.receiptNumber,
            date: resolveReceiptPrintDate(record, receipt),
            employeeFullName: record.creatorName || '',

            isCopy: (receipt.reprintCount ?? 0) > 0,
            reprintCount: receipt.reprintCount ?? 0,
            cancellationReceiptNumber: resolveOriginalReceiptNumberForPrint(record, receipt),

            positions: this._mapPositions(printPositions),
            taxes,

            totalGross: receipt.revenue ?? 0,
            totalNet: (receipt.revenue ?? 0) - totalTax,

            paymentType: RECEIPT_PAYMENT_TYPE_MAP[receipt.paymentType] ?? 'cash',

            serviceRecipient: this._mapServiceRecipient(record),

            tse: {
                printMessage: tseDoc?.fiscalResponse?.printMessage ?? '',
                // fiscalResponse.qrCode contains the full QR-code content (e.g. "V0;4-1;Kassenbeleg-V1;...").
                // fiscalResponse.signature is only the cryptographic bytes, NOT the QR-code string.
                qrCodeDataString: tseDoc?.fiscalResponse?.qrCode
                    ?? tseDoc?.fiscalResponse?.AdditionalFields?.['QrCodeDataString']
                    ?? '',
            },
        };
    }

    private _mapPositions(positions: InvoicePosition[]): ReceiptPosition[] {
        return (positions ?? []).map(pos => ({
            title: pos.title ?? '',
            subTitle: pos.subTitle || undefined,
            quantity: pos.quantity ?? 1,
            unitPrice: pos.price ?? 0,
            totalPrice: this._getPositionGross(pos),
            currency: 'EUR',
        }));
    }

    private _mapTaxes(taxPositions: any[]): ReceiptTax[] {
        return taxPositions
            .filter(tp => (tp.vatAmount ?? 0) !== 0)
            .map(tp => ({
                rate: tp.vatPercent1 ?? 0,
                taxValue: tp.vatAmount ?? 0,
            }));
    }

    /** Fallback: derive MwSt breakdown directly from invoice positions. */
    private _mapTaxesFromPositions(positions: InvoicePosition[]): ReceiptTax[] {
        const taxMap = new Map<number, number>();

        for (const pos of positions) {
            const rate = pos.taxRateValue ?? 0;
            if (rate <= 0) { continue; }
            const gross = this._getPositionGross(pos);
            const tax = gross - (gross / (1 + rate / 100));
            taxMap.set(rate, (taxMap.get(rate) ?? 0) + tax);
        }

        return Array.from(taxMap.entries()).map(([rate, taxValue]) => ({
            rate,
            taxValue: Math.round(taxValue * 100) / 100,
        }));
    }

    private _getPositionGross(pos: InvoicePosition): number {
        const totalPrice = (pos.quantity ?? 1) * (pos.price ?? 0) || 0;
        return totalPrice - (totalPrice / 100 * (pos.discountPercentage ?? 0));
    }


    private _mapServiceRecipient(record: BcmFinancialRecord): ReceiptServiceRecipient | undefined {
        const addressText = (record.address ?? '').trim();
        if (!addressText) { return undefined; }

        const lines = addressText.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
        if (lines.length === 0) { return undefined; }

        const hasPersonOrCompany = !!(record.person?.id ?? record.company?.id);
        // Bei manueller Adresse (ohne Stammdaten) immer erste Zeile als Name; bei Stammdaten
        // strukturierten Namen nutzen, aber bei leerem Wert auf erste Zeile zurückfallen (?? behandelt
        // leere Strings nicht, Person/Company können '' liefern).
        const name = hasPersonOrCompany
            ? ((record.person?.fullNameBackward || record.company?.name || '').trim() || lines[0])
            : lines[0];
        const addressLines = hasPersonOrCompany ? lines : lines.slice(1);

        return { name, addressLines };
    }

    // ─── Helpers ────────────────────────────────────────────────────────────────

    private _buildDocumentTitle(record: BcmFinancialRecord): string {
        const customer = record.person?.fullNameBackward ?? record.company?.name ?? '';
        return `${TranslationService.translate(record.type)} ${record.number} - ${customer}.pdf`;
    }

    private _showNoPdfDialog(record: BcmFinancialRecord): void {
        const dialogRef = this._matDialog.open(U2bInformationDialogComponent);
        dialogRef.componentInstance.dialogTitle = TranslationService.translate('bcm.financialRecords.dialog.printNotPossible');
        dialogRef.componentInstance.dialogBody =
            TranslationService.translate('bcm.financialRecords.error.noPdfForRecord', { type: TranslationService.translate(record.type) });
    }

    private _isGiantRelayPrintResponse(
        response: PrintJobCreateResponse,
    ): response is GiantRelayPrintResponse {
        return (response as GiantRelayPrintResponse).mode === 'giant-relay';
    }

    private _sendToLocalPrintRelay(response: GiantRelayPrintResponse): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const socket = new WebSocket(LOCAL_PRINT_RELAY_URL);
            let done = false;

            const closeSocket = () => {
                try {
                    socket.close();
                } catch (_e) {
                    // no-op
                }
            };

            const resolveOnce = (value: boolean) => {
                if (done) {
                    return;
                }
                done = true;
                clearTimeout(timeoutHandle);
                closeSocket();
                resolve(value);
            };

            // Sicherheitsnetz: Nimmt das Relay die Verbindung an, antwortet aber nicht
            // (z.B. hängende Drucker-Verbindung oder abgestürztes Relay), darf der
            // Checkout-Dialog nicht dauerhaft blockieren. Nach Ablauf brechen wir mit
            // Fehlermeldung ab, damit das Promise immer auflöst.
            const timeoutHandle = setTimeout(() => {
                window.alert(TranslationService.translate('bcm.print.error.relayTimeout'));
                resolveOnce(false);
            }, LOCAL_PRINT_RELAY_TIMEOUT_MS);

            socket.onopen = () => {
                socket.send(JSON.stringify({
                    ip: response.ip,
                    port: response.port,
                    data: response.data,
                }));
            };

            socket.onmessage = (event: MessageEvent) => {
                let relayResponse: { status?: string; message?: string } = {};
                try {
                    relayResponse = JSON.parse(String(event.data ?? '{}'));
                } catch (_e) {
                    // Wenn der Relay-Server kein JSON sendet, behandeln wir den Empfang als erfolgreich.
                }

                if (relayResponse?.status === 'error') {
                    window.alert(TranslationService.translate('bcm.print.error.printJobFailed', { message: relayResponse.message || TranslationService.translate('common.error.unknown') }));
                    resolveOnce(false);
                    return;
                }

                resolveOnce(true);
            };

            socket.onerror = () => {
                window.alert(TranslationService.translate('bcm.print.error.relayNotReachable'));
                resolveOnce(false);
            };

            socket.onclose = () => {
                resolveOnce(false);
            };
        });
    }
}
